import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getLoginRequest } from './msal-config.js';

/**
 * Returns a function that yields a current access token for the API, or null
 * when nobody is signed in.
 *
 * Silent first. MSAL serves a cached token while it is valid and refreshes it
 * without involving the person, which is what keeps a booking flow from being
 * interrupted. Only when the refresh genuinely needs them - consent changed, the
 * session expired - does it fall back to a redirect.
 *
 * One exported concept per file (NM-07), because every caller that talks to the
 * API needs this and none of them should reimplement the fallback.
 */
export function useAccessToken(): () => Promise<string | null> {
  const { instance, accounts } = useMsal();

  return useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return null;

    try {
      const result = await instance.acquireTokenSilent({ ...getLoginRequest(), account });
      return result.accessToken;
    } catch (cause) {
      if (cause instanceof InteractionRequiredAuthError) {
        // This navigates away. Nothing after it runs.
        await instance.acquireTokenRedirect({ ...getLoginRequest(), account });
        return null;
      }
      throw cause;
    }
  }, [instance, accounts]);
}
